import { localDateStr } from "./date";

const TERMINAL = new Set(["completed", "skipped", "missed", "cancelled", "rescheduled"]);
const statusOf = (row) => {
  const status = String(row.status || "scheduled").toLowerCase();
  return status === "canceled" ? "cancelled" : status;
};
const sourceOf = (row) => row.source || "auto";
const textOf = (value) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const unique = (values) => [...new Set(values.filter((value) => value !== null && value !== undefined && value !== ""))];

// Work in local civil days at noon: neither UTC parsing nor fixed-length days
// are safe for a calendar that crosses daylight-saving transitions.
function dateOf(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new RangeError("Invalid schedule date");
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) throw new RangeError("Schedule dates must use YYYY-MM-DD");
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  if (localDateStr(date) !== value) throw new RangeError("Invalid schedule date");
  return date;
}

/** Inclusive civil-date range. Weeks run Sunday through Saturday. */
export function getScheduleRange(selectedDate, view = "day") {
  const from = dateOf(selectedDate);
  const to = dateOf(selectedDate);
  if (view === "week") {
    from.setDate(from.getDate() - from.getDay());
    to.setDate(to.getDate() + 6 - to.getDay());
  } else if (view === "month") {
    from.setDate(1);
    to.setMonth(to.getMonth() + 1, 0);
  }
  return { from: localDateStr(from), to: localDateStr(to) };
}

/** Returns YYYY-MM-DD; a month move clamps to that month's last valid day. */
export function shiftScheduleDate(value, view, delta) {
  const date = dateOf(value);
  const amount = Number(delta);
  if (!Number.isInteger(amount)) throw new RangeError("Schedule shifts must be whole numbers");
  if (view === "month") {
    const day = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + amount);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12).getDate();
    date.setDate(Math.min(day, lastDay));
  } else {
    date.setDate(date.getDate() + amount * (view === "week" ? 7 : 1));
  }
  return localDateStr(date);
}

function aggregateStatus(services, todayStr, date) {
  const statuses = services.map(statusOf);
  const open = services.filter((row) => !TERMINAL.has(statusOf(row)));
  const remaining = open.length > 0;
  const completed = statuses.every((status) => status === "completed");
  const overdue = remaining && Boolean(todayStr) && date < todayStr;
  const inProgress = statuses.includes("started") || statuses.includes("partial")
    || (remaining && statuses.includes("completed"));
  let status;
  if (completed) status = "completed";
  else if (statuses.includes("started")) status = "started";
  else if (statuses.every((value) => value === "cancelled")) status = "cancelled";
  else if (statuses.every((value) => value === "skipped")) status = "skipped";
  else if (statuses.every((value) => value === "missed")) status = "missed";
  else if (statuses.includes("partial") || statuses.some((value) => TERMINAL.has(value))) status = "partial";
  else status = overdue ? "overdue" : "scheduled";
  return {
    status,
    remaining,
    completed,
    overdue,
    inProgress,
    unassigned: services.some((row) => !row.technician_id),
    needsTechnician: open.some((row) => !row.technician_id),
    attention: overdue || statuses.some((value) => value === "skipped" || value === "missed")
      || open.some((row) => !row.technician_id),
  };
}

/**
 * One display stop per client and civil day. Raw service objects and IDs remain
 * intact. Open actions with jobGroups[n].job: each group retains its own source,
 * because recurring and manual jobs for the same stop have separate write scopes.
 */
export function groupScheduleStops(jobs = [], clients = [], todayStr) {
  const clientById = new Map((clients || []).map((client) => [String(client.id), client]));
  const byStop = new Map();
  (jobs || []).forEach((job, index) => {
    if (!job || statusOf(job) === "rescheduled" || !job.scheduled_date) return;
    const date = job.scheduled_date;
    const clientId = job.client_id ?? null;
    // Missing clients must never collapse unrelated jobs into one stop.
    const identity = clientId ?? `missing-client:${job.id ?? index}`;
    const key = JSON.stringify([identity, date]);
    if (!byStop.has(key)) byStop.set(key, { key, date, clientId, services: [] });
    byStop.get(key).services.push(job);
  });

  return [...byStop.values()].map((stop) => {
    const { services } = stop;
    const client = clientById.get(String(stop.clientId)) || null;
    const firstValue = (field) => services.find((row) => row[field])?.[field];
    const name = client?.name || firstValue("client_name") || "Unknown client";
    const address = client?.address || firstValue("client_address") || "";
    const zip = client?.zip || firstValue("client_zip") || "";
    const bySource = new Map();
    services.forEach((job) => {
      const source = sourceOf(job);
      if (!bySource.has(source)) {
        bySource.set(source, { key: JSON.stringify([stop.key, source]), source, job, services: [] });
      }
      bySource.get(source).services.push(job);
    });
    const techIds = unique(services.map((row) => row.technician_id));
    const techNames = unique(services.map((row) => row.technician_name));
    const sequences = services.map((row) => row.job_headers?.route_seq ?? row.route_seq)
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map(Number).filter((value) => Number.isFinite(value) && value >= 0);
    const searchText = textOf([
      name, address, zip, stop.date, ...techNames,
      ...services.flatMap((row) => [row.id, row.service_type, sourceOf(row), row.notes, row.visit_notes]),
    ].filter(Boolean).join(" "));
    return {
      ...stop, client, name, address, zip,
      jobGroups: [...bySource.values()],
      ...aggregateStatus(services, todayStr, stop.date),
      techIds, techNames,
      routeSeq: sequences.length ? Math.min(...sequences) : null,
      searchText,
    };
  }).sort((a, b) => a.date.localeCompare(b.date)
    || (a.routeSeq ?? Infinity) - (b.routeSeq ?? Infinity)
    || a.name.localeCompare(b.name)
    || a.key.localeCompare(b.key));
}

/** Filters display stops without throwing away other services or source groups. */
export function filterScheduleStops(stops = [], filters = {}) {
  const { search = "", technician = "all", status = "all", service = "all", source = "all" } = filters;
  const terms = textOf(search).trim().split(/\s+/).filter(Boolean);
  return (stops || []).filter((stop) => {
    if (!terms.every((term) => stop.searchText.includes(term))) return false;
    // Require one service to satisfy assignment/type/source together; a manual
    // deodorizer must not match the technician of a different recurring cleanup.
    const matchesService = stop.services.some((row) => (
      (!technician || technician === "all" || (technician === "unassigned" ? !row.technician_id : String(row.technician_id) === String(technician)))
      && (!service || service === "all" || textOf(row.service_type) === textOf(service))
      && (!source || source === "all" || sourceOf(row) === source)
    ));
    if (!matchesService) return false;
    if (!status || status === "all") return true;
    if (status === "remaining") return stop.remaining;
    if (status === "completed") return stop.completed;
    if (status === "started") return stop.inProgress;
    if (status === "attention") return stop.attention;
    if (status === "overdue") return stop.overdue;
    if (status === "skipped_or_missed") return stop.services.some((row) => ["skipped", "missed"].includes(statusOf(row)));
    if (status === "skipped" || status === "missed" || status === "cancelled") {
      return stop.services.some((row) => statusOf(row) === status);
    }
    return stop.status === status;
  });
}

/** Stop counts remain distinct from the number of underlying service rows. */
export function getScheduleStats(stops = []) {
  return (stops || []).reduce((stats, stop) => ({
    total: stats.total + 1,
    completed: stats.completed + Number(stop.completed),
    remaining: stats.remaining + Number(stop.remaining),
    inProgress: stats.inProgress + Number(stop.inProgress),
    attention: stats.attention + Number(stop.attention),
    services: stats.services + stop.services.length,
  }), { total: 0, completed: 0, remaining: 0, inProgress: 0, attention: 0, services: 0 });
}
